/**
 * useBusinessPDFExport - Hook for exporting business profiles to PDF
 * Handles PDF generation and download for business profile data
 */

import { useState, useCallback } from 'react';
import { toast } from '@/hooks/useToast';
import type { Business } from '@/lib/types';
import type { ExportCompetitor } from '@/lib/pdf/business-pdf-generator';

export interface UseBusinessPDFExportReturn {
  /** Competitors are not on the business row, so the page passes what it fetched (#6717). */
  exportToPDF: (business: Business, competitors?: ExportCompetitor[], currencySymbol?: string) => Promise<void>;
  isExporting: boolean;
  progress: number;
  stage: string;
}

export function useBusinessPDFExport(): UseBusinessPDFExportReturn {
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState('');

  const exportToPDF = useCallback(async (business: Business, competitors: ExportCompetitor[] = [], currencySymbol = '') => {
    setIsExporting(true);
    setProgress(0);
    setStage('Preparing export...');

    try {
      // Lazy-load the PDF stack (jsPDF + svg2pdf + fonts) so it stays out of the main bundle
      const { generateBusinessPDF, generateBusinessPDFFilename } = await import(
        '@/lib/pdf/business-pdf-generator'
      );

      // Generate PDF
      const pdfBlob = await generateBusinessPDF({
        business,
        competitors,
        currencySymbol,
        onProgress: (stageText, progressValue) => {
          setStage(stageText);
          setProgress(progressValue);
        },
      });

      // Trigger download
      setStage('Downloading...');
      setProgress(95);

      const filename = generateBusinessPDFFilename(business);
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setProgress(100);
      setStage('Complete');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast({
        title: 'PDF export failed',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      // Reset state after a short delay
      setTimeout(() => {
        setIsExporting(false);
        setProgress(0);
        setStage('');
      }, 500);
    }
  }, []);

  return {
    exportToPDF,
    isExporting,
    progress,
    stage,
  };
}
